import React, { useState, useCallback } from 'react';
import { View, Text, StyleSheet, TextInput, TouchableOpacity } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useDeleteAccountMutation } from '@/store/api';
import { RESIDENTS_MOBILE_COLORS } from '@/constants/residents/mobile/color';
import { strings } from '@/constants/residents/strings';
import { useAppDispatch } from '@/store';
import { logout } from '@/store/slices/authSlice';
import { showAlert, showConfirm } from '@/utils/common/alert';

export const DeleteAccountScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteAccount] = useDeleteAccountMutation();

  const confirmAndSubmit = useCallback(async () => {
    // Show cross-platform destructive confirmation
    const confirmed = await showConfirm(
      strings.alert.deleteAccountTitle,
      strings.alert.deleteAccountMessage,
      strings.alert.deleteAccountAction,
      strings.alert.cancel
    );
    
    if (!confirmed) return;
    
    setIsSubmitting(true);
    try {
      const resp = await deleteAccount(undefined as any).unwrap();
      if ((resp as any)?.success) {
        navigation.navigate('DeleteAccountConfirmed');
      } else {
        showAlert(strings.alert.error, 'Failed to delete account');
      }
    } catch (e) {
      showAlert(strings.alert.error, 'Failed to delete account');
    } finally {
      setIsSubmitting(false);
    }
  }, [deleteAccount, navigation]);

  return (
    <View style={styles.container}>
      {/* Content */}
      <View style={styles.content}>
        {/* iOS warning and disclaimer copy */}
        <Text style={styles.warning}>{strings.profile.deleteAccount.warning}</Text>
        <Text style={styles.disclaimer}>{strings.profile.deleteAccount.disclaimer}</Text>
      </View>

      {/* Footer pinned to bottom */}
      <View style={styles.footer}>
        <TouchableOpacity style={styles.button} disabled={isSubmitting} onPress={confirmAndSubmit}>
          <Text style={styles.buttonText}>{isSubmitting ? 'Submitting...' : strings.profile.deleteAccount.button}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

export const DeleteAccountConfirmedScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  return (
    <View style={styles.container}>
      <Text style={styles.title}>{strings.profile.deleteConfirm.title}</Text>
      <Text style={styles.body}>{strings.profile.deleteConfirm.message}</Text>
      <Text style={styles.body}>{strings.profile.deleteConfirm.thanks}</Text>
      <TouchableOpacity style={styles.button} onPress={() => { dispatch(logout()); }}>
        <Text style={styles.buttonText}>{strings.profile.deleteConfirm.button}</Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: RESIDENTS_MOBILE_COLORS.BACKGROUND_PRIMARY,
    padding: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
  },
  body: {
    fontSize: 14,
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    opacity: 0.9,
    marginBottom: 12,
  },
  content: {
    flex: 1,
    justifyContent: 'flex-start',
  },
  warning: {
    fontSize: 18,
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    opacity: 0.95,
    marginBottom: 18,
    fontWeight: '700',
    textAlign: 'center',
    lineHeight: 24,
  },
  disclaimer: {
    fontSize: 13,
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    opacity: 0.9,
    textAlign: 'center',
  },
  footer: {
    paddingVertical: 16,
    marginBottom: 16,
  },
  input: {
    minHeight: 120,
    borderWidth: 1,
    borderColor: RESIDENTS_MOBILE_COLORS.BORDER_PRIMARY,
    borderRadius: 8,
    padding: 12,
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    backgroundColor: 'transparent',
    textAlignVertical: 'top',
  },
  button: {
    marginTop: 16,
    paddingVertical: 12,
    alignItems: 'center',
    backgroundColor: RESIDENTS_MOBILE_COLORS.BUTTON_SECONDARY,
  },
  buttonText: {
    color: RESIDENTS_MOBILE_COLORS.TEXT_SECONDARY,
    fontWeight: '600',
    fontSize: 14,
  },
});

export default DeleteAccountScreen;

